import { useCallback, useContext, useRef, useState } from 'react'

import { Box, Button, Icon, VStack } from '@chakra-ui/react'
import { AddIcon, BackIcon, CheckCircleIcon, ResetIcon, UndoIcon } from '@editor/assets/icons'
import { UserContext } from '@editor/contexts/Users'
import { InspectionArea } from '@editor/interfaces/inspection'
import { postPCDAlignment } from '@editor/services/PCDAlignment'
import { withPromiseContained } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { reloadAreas } from '@editor/store/page'
import { Tooltip } from '@infraloop/common'
import mixpanel from 'mixpanel-browser'
import { useSelector } from 'react-redux'

import { Steps, Tools } from '../definitions'
import { LOCAL_FLAGS, reset, resetAlignmentTool, resetLeftInspectionArea, setCurrentTool, undoAnchor } from '../store'

interface ButtonPanelProps {
  leftInspectionArea: InspectionArea | null
  rightInspectionArea: InspectionArea | undefined
}

const ButtonPanel = ({ leftInspectionArea, rightInspectionArea }: ButtonPanelProps) => {
  // Context
  const { getAccessToken } = useContext(UserContext)

  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionAreas = useSelector((state: RootState) => state.page.inspectionAreas)
  const currentStep = useSelector((state: RootState) => state.pcdAlignment.currentStep)
  const leftAnchors = useSelector((state: RootState) => state.pcdAlignment.leftAnchors)
  const rightAnchors = useSelector((state: RootState) => state.pcdAlignment.rightAnchors)
  const anchorHistory = useSelector((state: RootState) => state.pcdAlignment.anchorHistory)
  const transformMatrix = useSelector((state: RootState) => state.pcdAlignment.transformMatrix)
  const currentTool = useSelector((state: RootState) => state.pcdAlignment.currentTool)

  // States
  const [isSaving, setIsSaving] = useState(false)

  // Refs
  const containerRef = useRef<HTMLDivElement>(null)

  // Derived states
  const isSubmittable = currentStep === Steps.FineTuneOrSave

  // Handle save
  const onSave = useCallback(async () => {
    if (!isSubmittable || !leftInspectionArea || !rightInspectionArea || !project || !transformMatrix) return false

    // Defaults right as base and left as target.
    const baseArea = rightInspectionArea
    const baseAnchors = rightAnchors
    const targetArea = leftInspectionArea
    const targetAnchors = leftAnchors

    setIsSaving(true)
    return withPromiseContained(async () => {
      const token = await getAccessToken()
      if (!token) return

      await postPCDAlignment(token, project.project_id, {
        transformation: transformMatrix,
        target_area_id: targetArea.inspection_area_id,
        base_area_id: baseArea.inspection_area_id,
        target_anchor_positions: targetAnchors,
        base_anchor_positions: baseAnchors,
      })

      dispatch(reloadAreas())
      dispatch(reset())
    }).finally(() => {
      setIsSaving(false)
    })
  }, [
    isSubmittable,
    leftInspectionArea,
    rightInspectionArea,
    project,
    transformMatrix,
    leftAnchors,
    rightAnchors,
    getAccessToken,
    dispatch,
  ])

  return (
    <Box id="button-panel" ref={containerRef}>
      <VStack>
        {currentTool === Tools.PCDAlignment && (
          <>
            <Button
              data-testid="action-button-reset"
              variant="panel-dark"
              size={{
                base: 'md',
                md: 'md',
                lg: 'lg',
              }}
              fontSize="md"
              onClick={() => {
                dispatch(resetAlignmentTool())
                dispatch(setCurrentTool(Tools.Overview))
                mixpanel.track('Reset tool', {
                  Tool: 'PCD Alignment',
                  Reason: 'Reset button',
                })
              }}
              spinnerPlacement="end"
              width="full"
              justifyContent="space-between"
            >
              キャンセル
              <BackIcon />
            </Button>
            <Button
              data-testid="action-button-reset"
              variant="panel-dark"
              size={{
                base: 'md',
                md: 'md',
                lg: 'lg',
              }}
              fontSize="md"
              onClick={() => {
                dispatch(resetAlignmentTool())
                LOCAL_FLAGS.ignoreLeftCheck = true
              }}
              spinnerPlacement="end"
              width="full"
              justifyContent="space-between"
            >
              リセット
              <ResetIcon />
            </Button>
            <Button
              data-testid="action-button-undo"
              variant="panel-dark"
              size={{
                base: 'md',
                md: 'md',
                lg: 'lg',
              }}
              fontSize="md"
              disabled={anchorHistory.length === 0}
              onClick={() => {
                dispatch(undoAnchor())
              }}
              spinnerPlacement="end"
              width="full"
              justifyContent="space-between"
            >
              点をひとつ戻す
              <UndoIcon />
            </Button>
            <Button
              data-testid="action-button-save"
              colorPalette="blue"
              size={{
                base: 'md',
                md: 'md',
                lg: 'lg',
              }}
              fontSize="md"
              onClick={onSave}
              loading={isSaving}
              disabled={!isSubmittable || isSaving}
              spinnerPlacement="end"
              loadingText="保存中"
              width="full"
              justifyContent="space-between"
            >
              保存
              <Icon as={CheckCircleIcon} />
            </Button>
          </>
        )}

        {currentTool === Tools.Overview && (
          <Tooltip
            showArrow
            label={
              inspectionAreas.length <= 1
                ? // ? 'This project has only one area. Upload more areas to use this feature'
                  'この工事には1つのエリアしかありません。この機能を使用するには2つ以上のエリアが必要です。'
                : undefined
            }
            portalRef={containerRef}
          >
            <Button
              disabled={inspectionAreas.length <= 1}
              data-testid="action-button-add"
              colorPalette="blue"
              size={{
                base: 'md',
                md: 'md',
                lg: 'lg',
              }}
              fontSize="md"
              onClick={() => {
                dispatch(resetAlignmentTool())
                dispatch(resetLeftInspectionArea())
                dispatch(setCurrentTool(Tools.PCDAlignment))
              }}
              spinnerPlacement="end"
              width="full"
              justifyContent="space-between"
            >
              {/* Add Connection */}
              新規追加/編集
              <AddIcon />
            </Button>
          </Tooltip>
        )}
      </VStack>
    </Box>
  )
}

export default ButtonPanel
